import { computed, Ref } from 'vue';

interface SortState {
  [key: string]: {
    sortOrder: number;
    sortPriority: number;
  };
}

function useAnswerSortItem<T>(sortState: Ref<SortState>, items: Ref<T[]>) {
  const toggleSort = (sortName: string) => {
    const newState = (sortState.value[sortName].sortOrder + 1) % 3;
    sortState.value[sortName].sortOrder = newState;

    if (sortState.value[sortName].sortPriority === 0) {
      const maxPriority = Math.max(
        0,
        ...Object.values(sortState.value).map(s => s.sortPriority)
      );
      sortState.value[sortName].sortPriority = maxPriority + 1;
    } else if (newState === 0) {
      const currentPriority = sortState.value[sortName].sortPriority;
      sortState.value[sortName].sortPriority = 0;

      Object.values(sortState.value).forEach(s => {
        if (s.sortPriority > currentPriority) {
          s.sortPriority -= 1;
        }
      });
    }
  };

  function getValueFlexible(obj: any, key: string): any {
    if (!obj || typeof obj !== 'object') return undefined;

    // 1. Check top-level property
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      return obj[key];
    }

    // 2. Check each nested object at top level
    for (const prop in obj) {
      if (
        obj[prop] &&
        typeof obj[prop] === 'object' &&
        Object.prototype.hasOwnProperty.call(obj[prop], key)
      ) {
        return obj[prop][key];
      }
    }

    return undefined;
  }

  const sortedItems = computed(() => {
    const originItems = [...items.value]; //初期状態に戻す

    function mapValueToSortNumber(
      value: boolean | undefined,
      sortOrder: number
    ) {
      if (sortOrder === 1) {
        if (value === true) {
          return 1;
        } else if (value === false) {
          return 2;
        }
        return 3;
      } else {
        if (value === true) {
          return 2;
        } else if (value === false) {
          return 3;
        }
        return 1;
      }
    }

    //sortOrderが0でない項目を抽出し、sortPriorityに基づいて並び替え
    const sortCriteria = Object.entries(sortState.value)
      .filter(
        ([, { sortOrder, sortPriority }]) =>
          sortOrder !== 0 && sortPriority !== 0
      )
      .sort((a, b) => a[1].sortPriority - b[1].sortPriority);

    //ソート処理
    originItems.sort((a, b) => {
      for (const [key, { sortOrder }] of sortCriteria) {
        let valA = getValueFlexible(a, key),
          valB = getValueFlexible(b, key);

        //booleanとundefinedを数字にする。
        valA =
          typeof valA === 'boolean' || typeof valA === 'undefined'
            ? mapValueToSortNumber(valA, sortOrder)
            : valA;
        valB =
          typeof valB === 'boolean' || typeof valB === 'undefined'
            ? mapValueToSortNumber(valB, sortOrder)
            : valB;

        //数値としての比較、型に応じて比較方法を変える
        if (
          (typeof valA === 'number' && typeof valB === 'number') ||
          (typeof valA === 'string' && typeof valB === 'string')
        ) {
          let comparison = 0;
          if (typeof valA === 'number' && typeof valB === 'number') {
            //数値として比較
            comparison = sortOrder === 1 ? valA - valB : valB - valA;
          } else if (typeof valA === 'string' && typeof valB === 'string') {
            //文字列として比較
            comparison =
              sortOrder === 1
                ? valA.localeCompare(valB)
                : valB.localeCompare(valA);
          }
          if (comparison !== 0) return comparison;
        } else {
          //文字列と数値が混在している場合、すべて文字列として扱い、localeCompareで比較
          const comparison =
            sortOrder === 1
              ? String(valA).localeCompare(String(valB), undefined, {
                  numeric: true,
                })
              : String(valB).localeCompare(String(valA), undefined, {
                  numeric: true,
                });
          if (comparison !== 0) return comparison;
        }
      }
      return 0; //すべてのソート条件で等しい、またはソート条件がない
    });

    return originItems;
  });

  return { toggleSort, sortedItems };
}

export default useAnswerSortItem;
